import React, { useRef } from 'react';
import {
    Button,
    Checkbox,
    ChoiceCard,
    DropdownMenu,
    SparkIcon,
    TagColored,
    TagFeedback,
    Text,
} from '@spark';
import { mappingComplete, recordsLabel } from '../../utils/revaluateFlow';

const ACCEPT = '.csv,.json,.jsonl';

function SectionTitle({ children, hint }) {
    return (
        <div className="revaluate-settings__section-title">
            <Text as="span" variant="base-semibold" color="primary">{children}</Text>
            {hint ? <Text as="span" variant="detail-regular" color="tertiary">{hint}</Text> : null}
        </div>
    );
}

function RowCopy({ name, meta, metaClassName = '' }) {
    return (
        <span className="revaluate-row__copy">
            <Text as="span" variant="base-medium" color="primary" className="revaluate-row__name">{name}</Text>
            {meta ? (
                <Text as="span" variant="detail-regular" color="tertiary" className={`revaluate-row__meta ${metaClassName}`.trim()}>
                    {meta}
                </Text>
            ) : null}
        </span>
    );
}

/** One selected benchmark (Figma 35:2222): checkbox, name, "1,200 records · Linked to task", optional Updated tag. */
function BenchmarkRow({ benchmark, onToggle }) {
    const meta = [recordsLabel(benchmark.records), benchmark.linked ? 'Linked to task' : '']
        .filter(Boolean)
        .join(' · ');
    return (
        <div className="revaluate-row">
            <Checkbox label="" status="active" aria-label={benchmark.name} onChange={onToggle} />
            <RowCopy name={benchmark.name} meta={meta} />
            {benchmark.updated ? <TagColored variant="yellow" label="Updated" /> : null}
        </div>
    );
}

/** Upload row states (Figma 35:2275): Validating, Valid and Error. */
function UploadRow({ draftFile, fileStatus, fileMessage, pendingName, onRemove }) {
    if (fileStatus === 'validating') {
        return (
            <div className="revaluate-row" role="status">
                <SparkIcon slug="file-upload" size="medium" className="revaluate-row__icon" />
                <RowCopy name={pendingName || 'File'} meta="Checking columns and records…" />
                <TagFeedback label="Checking" variant="default" />
            </div>
        );
    }
    if (fileStatus === 'error') {
        const message = `${fileMessage || 'This file cannot be used.'} This file won't be included in the evaluation.`;
        return (
            <div className="revaluate-row revaluate-row--error" role="alert">
                <SparkIcon slug="file-upload" size="medium" className="revaluate-row__icon" />
                <RowCopy name={pendingName || 'File'} meta={message} metaClassName="revaluate-row__meta--error" />
                <TagFeedback label="Can't use file" variant="error" showIcon />
                <Button variant="ghost" size="small" type="button" onClick={onRemove}>Remove</Button>
            </div>
        );
    }
    if (draftFile?.ok) {
        const fileName = draftFile.file?.name || draftFile.name;
        const meta = [recordsLabel(draftFile.records), 'Columns match the task'].filter(Boolean).join(' · ');
        return (
            <div className="revaluate-row">
                <Checkbox label="" status="active" aria-label={fileName} onChange={onRemove} />
                <RowCopy name={fileName} meta={meta} />
                <TagFeedback label="Ready" variant="success" showIcon />
            </div>
        );
    }
    return null;
}

/** Field mapping (Figma 226:14189): auto-mapped line with Edit, or the column selects with Done. */
function MappingSection({ fields, columns, mapping, showMapping, onMappingChange, onEditMapping, onMappingDone }) {
    const mapped = fields.filter((field) => mapping[field]);
    if (!showMapping) {
        return (
            <div className="revaluate-mapping__auto">
                <SparkIcon slug="check" size="small" className="revaluate-mapping__check" />
                <Text as="span" variant="detail-regular" color="secondary">
                    {mapped.length === fields.length
                        ? `${mapped.length} ${mapped.length === 1 ? 'field' : 'fields'} mapped automatically: ${mapped.join(', ')}`
                        : `${mapped.length} of ${fields.length} fields mapped`}
                </Text>
                <Button variant="ghost" size="tiny" type="button" onClick={onEditMapping}>Edit</Button>
            </div>
        );
    }
    const complete = mappingComplete(mapping, fields);
    return (
        <div className="revaluate-mapping" aria-label="Field mapping">
            <div className="revaluate-mapping__head">
                <span className="revaluate-mapping__copy">
                    <Text as="span" variant="detail-medium" color="primary">Map prompt fields</Text>
                    <Text as="span" variant="detail-regular" color="secondary">
                        Choose the file column for each field. Input and output follow the task settings.
                    </Text>
                </span>
                <Button variant="ghost" size="small" type="button" disabled={!complete} onClick={onMappingDone}>
                    Done
                </Button>
            </div>
            <div className="revaluate-mapping__rows">
                {fields.map((field) => (
                    <div key={field} className="revaluate-mapping__row">
                        <Text as="span" variant="detail-medium" color="primary" className="revaluate-mapping__label">
                            {field}
                        </Text>
                        <DropdownMenu
                            fill
                            label={field}
                            showLabel={false}
                            placeholder="Select column"
                            text={mapping[field] || undefined}
                            value={mapping[field] || undefined}
                            items={columns.map((column) => ({
                                label: column,
                                onClick: () => onMappingChange(field, column),
                            }))}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * Evaluation settings (Figma 98:13302 / 98:13947 / 103:16589 / 151:21277):
 * what to run, the benchmark rows, upload / library actions, file mapping and
 * the responses note.
 */
export default function RevaluateSettingsView({
    cards,
    recommendedScope,
    scope,
    onScopeChange,
    lockNote,
    benchmarks,
    onToggleBenchmark,
    canAddBenchmarks,
    onOpenLibrary,
    onPickFile,
    draftFile,
    fileStatus,
    fileMessage,
    pendingName,
    onRemoveFile,
    fields,
    mapping,
    showMapping,
    onMappingChange,
    onEditMapping,
    onMappingDone,
    noteTitle,
    noteText,
}) {
    const fileInputRef = useRef(null);
    const showMappingBlock = draftFile?.ok && fields.length > 0;
    const listEmpty = benchmarks.length === 0 && !draftFile?.ok && fileStatus === 'idle';

    return (
        <div className="revaluate-settings">
            <section className="revaluate-settings__section" aria-label="What to run">
                <SectionTitle>What to run</SectionTitle>
                <div className="revaluate-settings__cards" role="group" aria-label="Evaluation scope">
                    {cards.map((card) => (
                        <div key={card.id} className="revaluate-settings__card">
                            <ChoiceCard
                                label={card.title}
                                description={card.body}
                                selected={scope === card.id}
                                disabled={card.locked}
                                data-value={card.id}
                                onClick={() => onScopeChange(card.id)}
                            />
                            {recommendedScope === card.id && !card.locked ? (
                                <TagColored
                                    variant="burgundy"
                                    label="Recommended"
                                    className="revaluate-settings__recommended"
                                />
                            ) : null}
                        </div>
                    ))}
                </div>
                {lockNote ? (
                    <Text as="p" variant="detail-regular" color="secondary">{lockNote}</Text>
                ) : null}
            </section>

            <section className="revaluate-settings__section" aria-label="Benchmarks">
                <SectionTitle hint="Select the benchmarks to evaluate on.">Benchmarks</SectionTitle>
                <div className="revaluate-settings__list" role="group" aria-label="Benchmark list">
                    {benchmarks.map((benchmark) => (
                        <BenchmarkRow
                            key={benchmark.name}
                            benchmark={benchmark}
                            onToggle={() => onToggleBenchmark(benchmark.name)}
                        />
                    ))}
                    <UploadRow
                        draftFile={draftFile}
                        fileStatus={fileStatus}
                        fileMessage={fileMessage}
                        pendingName={pendingName}
                        onRemove={onRemoveFile}
                    />
                    {listEmpty ? (
                        <Text as="p" variant="detail-regular" color="tertiary">No benchmarks selected.</Text>
                    ) : null}
                </div>
                {showMappingBlock ? (
                    <MappingSection
                        fields={fields}
                        columns={draftFile.columns || []}
                        mapping={mapping}
                        showMapping={showMapping}
                        onMappingChange={onMappingChange}
                        onEditMapping={onEditMapping}
                        onMappingDone={onMappingDone}
                    />
                ) : null}
                {canAddBenchmarks ? (
                    <div className="revaluate-settings__actions">
                        <Button
                            variant="tertiary"
                            size="medium"
                            type="button"
                            startIcon={<SparkIcon slug="upload" size="small" />}
                            disabled={fileStatus === 'validating'}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Upload file
                        </Button>
                        <Button
                            variant="tertiary"
                            size="medium"
                            type="button"
                            startIcon={<SparkIcon slug="library" size="small" />}
                            onClick={onOpenLibrary}
                        >
                            Choose From Uploads
                        </Button>
                        <input
                            ref={fileInputRef}
                            aria-label="Upload benchmark"
                            type="file"
                            accept={ACCEPT}
                            className="revaluate-settings__file-input"
                            onChange={(event) => {
                                const file = event.target.files?.[0];
                                event.target.value = '';
                                void onPickFile(file);
                            }}
                        />
                    </div>
                ) : (
                    <Text as="p" variant="detail-regular" color="secondary">
                        To add benchmarks, switch to Full evaluation. New benchmarks need results from both models.
                    </Text>
                )}
            </section>

            {noteText ? (
                <section className="revaluate-settings__section" aria-label={noteTitle}>
                    <SectionTitle>{noteTitle}</SectionTitle>
                    <Text as="p" variant="detail-regular" color="secondary">{noteText}</Text>
                </section>
            ) : null}
        </div>
    );
}
